import { Button, Layout, ReportData, ReportPreviewTabSource, SlideoutDecorator, StepEvent, Switch } from "@mcleod/components";
import { AutogenLayoutMiscInvoiceProcessingStepper } from "./autogen/AutogenLayoutMiscInvoiceProcessingStepper";
import { Api, ModelRow } from "@mcleod/core";
import { BackOfficeReportPreview } from "@mcleod/settlement/src/BackOfficeReportPreview";
import { CommonDialogs } from "@mcleod/common";
import { MiscInvoiceProcessingUtil } from "./MiscInvoiceProcessingUtil";

export enum MiscInvoiceProcessingAction {
    PRINT_AND_POST,
    PRINT_ONLY,
    POST_ONLY
}

export enum MiscInvoiceProcessingStep {
    EDIT_LIST_STEP = 0,
    PRINT_REVIEW_STEP = 1,
    PRE_POST_REVIEW_STEP = 2,
    POSTING_REPORTS_STEP = 3
}

export interface MiscInvoiceProcessingStepperProps {
    action: MiscInvoiceProcessingAction;
    selectedRows: ModelRow[];
    sendToImaging?: boolean;
    doAfterSlideOut?: (postingCompleted: boolean, postedCount: number) => void;
}

export class MiscInvoiceProcessingStepper extends AutogenLayoutMiscInvoiceProcessingStepper {
    private minAllowedStepIndex: number = 0;
    private maxAllowedStepIndex: number = 0;
    private reportPreviewEditList: BackOfficeReportPreview;
    private reportPreviewPostingReports: BackOfficeReportPreview;
    private _switchPrintToImaging: Switch;
    private _buttonContinue: Button;
    private globalProps: MiscInvoiceProcessingStepperProps;

    // postingAttempted is used in doAfterSlideOut to indicate that the posting process was not cancelled before posting.
    // It will be true even if no invoices were posted due to exceptions.
    private postingAttempted: boolean = false;
    private postedCount: number = 0;

    static showAsSlideout(props: MiscInvoiceProcessingStepperProps) {
        const stepperLayout = Layout.getLayout("lme/ar/MiscInvoiceProcessingStepper") as MiscInvoiceProcessingStepper;

        SlideoutDecorator.decorateAndSlideIn(stepperLayout, {
            title: `Print/Post (${props.selectedRows.length}) Records`,
            fillVerticalSpace: true,
            width: "60%",
            addlComponents: [ stepperLayout.buttonContinue ],
            overlayProps: { greyedBackground: true },
            layoutLoadListeners: () => {
                stepperLayout.globalProps = props;
                stepperLayout.initializeEditListPanel();
                stepperLayout.initializePostingReportsPanel();
            },
            doAfterSlideIn: async () => {
                switch (props.action) {
                    case MiscInvoiceProcessingAction.PRINT_ONLY: {
                        stepperLayout.minAllowedStepIndex = MiscInvoiceProcessingStep.PRINT_REVIEW_STEP;
                        stepperLayout.updateStep(MiscInvoiceProcessingStep.PRINT_REVIEW_STEP);
                        stepperLayout.globalProps.sendToImaging = await CommonDialogs.showYesNo("Send selected invoice(s) to Imaging?", "Send to Imaging");
                        stepperLayout.layoutMiscInvoiceProcessingPrintReview.printInvoices(stepperLayout.globalProps);
                        break;
                    }
                    case MiscInvoiceProcessingAction.POST_ONLY: {
                        // Must simulate advancing through prior steps to properly set up the stepper
                        stepperLayout.minAllowedStepIndex = MiscInvoiceProcessingStep.PRINT_REVIEW_STEP;
                        stepperLayout.updateStep(MiscInvoiceProcessingStep.PRINT_REVIEW_STEP);
                        stepperLayout.minAllowedStepIndex = MiscInvoiceProcessingStep.PRE_POST_REVIEW_STEP;
                        stepperLayout.updateStep(MiscInvoiceProcessingStep.PRE_POST_REVIEW_STEP);
                        stepperLayout.minAllowedStepIndex = MiscInvoiceProcessingStep.POSTING_REPORTS_STEP;
                        stepperLayout.updateStep(MiscInvoiceProcessingStep.POSTING_REPORTS_STEP);

                        stepperLayout.postInvoices(stepperLayout.globalProps);
                        break;
                    }
                    case MiscInvoiceProcessingAction.PRINT_AND_POST: {
                        stepperLayout.printEditList(props);
                        break;
                    }
                }
            },
            doAfterSlideOut: () => {
                if (props.doAfterSlideOut) {
                    props.doAfterSlideOut(stepperLayout.postingAttempted, stepperLayout.postedCount);
                }
            }
        });
    }

    get buttonContinue(): Button {
        if (!this._buttonContinue) {
            this.createContinueButton();
        }
        return this._buttonContinue;
    }

    private createContinueButton() {
        if (this._buttonContinue == null) {
            this._buttonContinue = new Button({
                id: "buttonContinue",
                caption: "Generate Invoices",
                color: "primary.reverse",
                backgroundColor: "primary",
                width: 140,
                rowBreak: false,
                borderWidth: 0,
                onClick: () => {
                    this.advanceToNextStep();
                }
            });
        }
    }

    get switchPrintToImaging(): Switch {
        if (!this._switchPrintToImaging) {
            this.createPrintToImagingSwitch();
        }
        return this._switchPrintToImaging;
    }

    private createPrintToImagingSwitch() {
        if (this._switchPrintToImaging == null) {
            this._switchPrintToImaging = new Switch({
                id: "switchPrintToImaging",
                caption: "Print To Imaging",
                checked: true,
                leftCaption: "No",
                rightCaption: "Yes",
                marginRight: 8,
                rowBreak: false
            });
        }
    }

    stepperMiscInvoiceProcessingBeforeStepListener(event: StepEvent) {
        const stepIndex = event.newIndex;
        // Prevent advancing to next step without using the continue button first, and prevent going back beyond allowed steps
        if (stepIndex > this.maxAllowedStepIndex || stepIndex < this.minAllowedStepIndex) {
            event.preventDefault();
        } else {
            this.updateContinueButtonCaption(stepIndex);
        }
    }

    // Should only be called via the Continue button's onClick handler to ensure proper step tracking
    async advanceToNextStep() {
        if (this.stepEditList.selected) {
            this.globalProps.sendToImaging = this.switchPrintToImaging.checked;
            this.updateStep(MiscInvoiceProcessingStep.PRINT_REVIEW_STEP);
            this.buttonContinue.busy = true;
            await this.layoutMiscInvoiceProcessingPrintReview.printInvoices(this.globalProps);
            this.buttonContinue.busy = false;
            this.updateContinueButtonCaption(MiscInvoiceProcessingStep.PRINT_REVIEW_STEP);
        } else if (this.stepPrintReview.selected) {
            this.updateStep(MiscInvoiceProcessingStep.PRE_POST_REVIEW_STEP);
            this.buttonContinue.busy = true;
            await this.layoutMiscInvoiceProcessingPrePostReview.confirmPrint(this.globalProps);
            this.buttonContinue.busy = false;
            this.updateContinueButtonCaption(MiscInvoiceProcessingStep.PRE_POST_REVIEW_STEP);
        } else if (this.stepPrePostReview.selected) {
            this.updateStep(MiscInvoiceProcessingStep.POSTING_REPORTS_STEP);
            this.buttonContinue.busy = true;
            await this.postInvoices(this.globalProps);
            this.buttonContinue.busy = false;
            this.updateContinueButtonCaption(MiscInvoiceProcessingStep.POSTING_REPORTS_STEP);
        } else if (this.stepPostingReports.selected) {
            this.slideOut();
        }
    }

    updateStep(newIndex: number) {
        this.updateMaxAllowedIndex(newIndex);
        this.stepperMiscInvoiceProcessing.selectedIndex = newIndex;
    }

    updateMaxAllowedIndex(newIndex: number) {
        if (newIndex > this.maxAllowedStepIndex) {
            this.maxAllowedStepIndex = newIndex;
        }
    }

    updateContinueButtonCaption(newIndex: number) {
        switch (newIndex) {
            case MiscInvoiceProcessingStep.EDIT_LIST_STEP:
                this.buttonContinue.caption = "Generate Invoices";
                break;
            case MiscInvoiceProcessingStep.PRINT_REVIEW_STEP:
                this.buttonContinue.caption = "Confirm Print";
                break;
            case MiscInvoiceProcessingStep.PRE_POST_REVIEW_STEP:
                this.buttonContinue.caption = "Post";
                break;
            case MiscInvoiceProcessingStep.POSTING_REPORTS_STEP:
                this.buttonContinue.caption = "Close";
                break;
        }
    }

    // START: Edit List Step logic
    private initializeEditListPanel() {
        this.reportPreviewEditList = new BackOfficeReportPreview("Edit List", true);
        this.reportPreviewEditList.insertFirstRightHeaderTool(this.switchPrintToImaging);
        this.stepEditList.add(this.reportPreviewEditList);
    }

    private async printEditList(props: MiscInvoiceProcessingStepperProps) {
        this.buttonContinue.busy = true;
        const response = await Api.post("lme/ar/print-misc-invoice-edit-list", { misc_invoices: props.selectedRows });
        this.buttonContinue.busy = false;

        const data = response?.data[0];
        this.reportPreviewEditList.beginSearch();
        const exceptionTab = MiscInvoiceProcessingUtil.createExceptionTab(data.exceptions_list);

        const tabSources: ReportPreviewTabSource[] = [
            ...data.all_report_data as ReportData[],
            exceptionTab
        ];
        this.reportPreviewEditList.endSearch(MiscInvoiceProcessingUtil.cleanTabSources(tabSources));
    }
    // END: Edit List Step logic

    // START: Posting Reports Step logic
    private initializePostingReportsPanel() {
        this.reportPreviewPostingReports = new BackOfficeReportPreview("Posting Reports", true);
        this.stepPostingReports.add(this.reportPreviewPostingReports);
    }

    private async postInvoices(props: MiscInvoiceProcessingStepperProps) {
        const response = await Api.post("lme/ar/post-misc-invoices", { misc_invoices: props.selectedRows });
        const data = response?.data[0];

        this.reportPreviewPostingReports.beginSearch();
        const exceptionTab = MiscInvoiceProcessingUtil.createExceptionTab(data.exceptions_list);

        const tabSources: ReportPreviewTabSource[] = [
            ...data.all_report_data as ReportData[],
            exceptionTab
        ];
        this.reportPreviewPostingReports.endSearch(MiscInvoiceProcessingUtil.cleanTabSources(tabSources));

        this.postingAttempted = true;
        this.postedCount = data.posted_count || 0;
    }
    // END: Posting Reports Step logic
}
